/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import {
	Filters,
	GlobalAlert,
	Header,
	PERMISSIONS,
	Page,
	PageAuth,
	PageContainer,
	PageHeader,
	SideMenu,
	Sidebar,
} from '../..';
import { CASINO_GAMES_SEARCH_TYPES, gamesPages, useStore } from '../../../common';
import { v4 as uuid } from 'uuid';
import { AuthContext } from '../../../logic';
import { Button, Checkbox, Dropdown, Modal, Pagination, PlusLight, Table } from 'esa-ui-library';
import { CategoriesFilter, PartnersFilter, ProvidersFilter, SearchFilter } from '../../components/Filters/components';
import styles from './Games.module.scss';
import { getRows } from './RowProvider';
import { gamesListMap } from './Games.const';
import { filterFieldsByEnv, filterSearchOptionsByEnv } from './Games.helper';
import { PropertyType } from 'esa-ui-library/lib/types';
import { GeneralFilter } from '../../components/Filters/components/GeneralFilter/GeneralFilter';
import { SAVE } from '../GamblingReport/ReportTemplates/ReportTemplates.const';
import { CreateGameForm } from './components/CreateGameForm';
import { AddCategoryForm } from './components/AddCategoryForm';
import { ImportGameForm } from './components/ImportGameForm';
import { AssignPartnersForm } from './components/AssignPartnersForm';
import { BulkActions } from './components';
import { BulkAssignPartnersForm } from './components/BulkAssignPartnersForm';
import { BulkEnableGamesForm } from './components/BulkEnableGamesForm';
import { ShowPartners } from './components/ShowPartners';
import { TableActions } from '../../components/TableActions/TableActions';
import { getFiltersUrl } from '../PlayerList/PlayerList.helper';
import { tableActions } from '../../components/TableActions/TableActions.const';
import { getCasinoGamesExport } from '../../../api/endpoints/getCasinoGames';

const Games = (): JSX.Element => {
	const [state, dispatch] = useStore();
	const [categoryModal, setCategoryModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [assignPartnersModal, setAssignPartnersModal] = useState(false);
	const [ImportGameModal, setImportGameModal] = useState(false);
	const [BulkAssignPartnersModal, setBulkAssignPartnersModal] = useState(false);
	const [BulkEnableGamesModal, setBulkEnableGamesModal] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);
	const [showPartnersModal, setShowPartnersModal] = useState(false);
	const {
		games,
		rows,
		bulkRows,
		columns,
		pagesTotal,
		currentPage,
		gamesLoading,
		perPage,
		selectedGame,
		categoryForm,
		gameForm,
		importGameForm,
		bulkAssignPartnersForm,
		bulkEnableGamesForm,
		gamePartnersForm,
		AAMS_types,
		filters,
	} = state;
	const { user, token } = useContext(AuthContext);
	const filteredGamesFields = filterFieldsByEnv(gamesListMap);

	const allRowsSelected = !!rows.length && rows.length === bulkRows.length;

	const onBulkAllChange = () => {
		dispatch('BULK_ALL_CHANGE');
	};

	const onAction = (type: string, game: PropertyType) => {
		switch (type) {
			case 'edit':
				dispatch('SELECT_GAME', { game });
				setEditModal(true);
				break;
			case 'category':
				dispatch('SELECT_GAME', { game });
				setCategoryModal(true);
				break;
			case 'import':
				setImportGameModal(true);
				break;
			case 'enable':
				dispatch('GAMES_ENABLE', { token });
				break;
			case 'partners':
				dispatch('SELECT_GAME', { game });
				setAssignPartnersModal(true);
				dispatch('GET_PARTNERS', { token });
				break;
			case 'showPartners':
				dispatch('SELECT_GAME', { token, game });
				setShowPartnersModal(true);
				break;
			default:
				break;
		}
	};

	const setColumnsConfig = (columns: PropertyType[]) =>
		columns.map((c) => {
			return {
				...c,
				cellClassName: c.position ? styles[c.position] : '',
			};
		});

	const addBulkAll = () => {
		return [
			<Checkbox
				key={uuid()}
				checked={allRowsSelected}
				partial={!!bulkRows.length && !allRowsSelected}
				onChange={onBulkAllChange}
				dataId='bulk-check'
			/>,
			...setColumnsConfig(filteredGamesFields),
			'',
		];
	};

	useEffect(() => {
		dispatch('LOADING', true);
		dispatch('GAMES_INIT', { token, currentPage, perPage });
		dispatch('PARTNERS_INIT', user);
		dispatch('FILTERS_INIT', {
			token,
			filters: ['providers', 'gameCategories', 'checksumStatuses', 'AAMS_types'],
		});
	}, []);

	useEffect(() => {
		dispatch('UPDATE_COLUMNS', addBulkAll());
	}, [games, bulkRows]);

	useEffect(() => {
		dispatch('UPDATE_ROWS', getRows(games, columns, onAction, AAMS_types));
	}, [games, columns, AAMS_types]);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	const onAddGame = () => {
		setEditModal(true);
	};

	const onImportGameSubmit = async () => {
		await dispatch('GAMES_IMPORT', { token, importGameForm });
		dispatch('UPDATE_STATE', { infoText: state.infoText });
		if (state.formErrors) {
			dispatch('MESSAGE_INVALIDATE');
		}
		if (state.infoText === undefined) {
			setImportGameModal(false);
		}
	};

	const onBulkAssignPartnersSubmit = async () => {
		await dispatch('ASSIGN_PARTNERS_IMPORT', { token, bulkAssignPartnersForm });
		dispatch('UPDATE_STATE', { infoText: state.infoText });
		if (state.formErrors) {
			dispatch('MESSAGE_INVALIDATE');
		}
		if (state.infoText === undefined) {
			setBulkEnableGamesModal(false);
		}
		dispatch('FORM_BULK_ASSIGN_PARTNERS_RESET');
		setBulkAssignPartnersModal(false);
	};

	const onBulkEnableGamesSubmit = async () => {
		await dispatch('ENABLE_GAMES_IMPORT', { token, bulkEnableGamesForm });
		dispatch('UPDATE_STATE', { infoText: state.infoText });
		if (state.formErrors) {
			dispatch('MESSAGE_INVALIDATE');
		}
		if (state.infoText === undefined) {
			setBulkEnableGamesModal(false);
		}
		dispatch('FORM_BULK_ASSIGN_PARTNERS_RESET');
	};

	const onCloseCreateModal = () => {
		setEditModal(false);
		dispatch('SELECT_GAME', { game: null });
		dispatch('FORM_RESET');
		dispatch('MESSAGE_INVALIDATE');
	};

	const onCategoryModalClose = () => {
		setCategoryModal(false);
		dispatch('SELECT_GAME', { game: null });
		dispatch('FORM_CATEGORY_RESET');
	};

	const onImportGameModalClose = () => {
		setImportGameModal(false);
		dispatch('FORM_IMPORT_GAME_RESET');
		dispatch('INFO_TEXT');
	};

	const onBulkAssignPartnersModalClose = () => {
		setBulkAssignPartnersModal(false);
		dispatch('FORM_BULK_ASSIGN_PARTNERS_RESET');
		dispatch('INFO_TEXT');
	};

	const onBulkEnableGamesModalClose = () => {
		setBulkEnableGamesModal(false);
		dispatch('FORM_BULK_ENABLE_GAMES_RESET');
		dispatch('INFO_TEXT');
	};

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	const onSubmit = async () => {
		setLoading(true);
		await dispatch('GAMES_ADD', { token, gameForm });
		setEditModal(false);
		dispatch('FORM_RESET');
		dispatch('SELECT_GAME', { game: null });
		setLoading(false);
	};

	const onAddCategory = async () => {
		setLoading(true);
		await dispatch('GAMES_CATEGORIES_ADD', { token, form: categoryForm, game_id: selectedGame.internal_game_id });
		setCategoryModal(false);
		setLoading(false);
	};

	const onAssignPartnersSubmit = async () => {
		setLoading(true);

		await dispatch('UPDATE_STATE', { formErrors: {} });

		await dispatch('GAME_PARTNERS_ASSIGN', {
			token,
			form: gamePartnersForm,
			game_id: selectedGame.internal_game_id,
		});

		dispatch('FORM_PARTNERS_RESET');
		setAssignPartnersModal(false);
		setLoading(false);
	};

	const onCloseAssignPartners = () => {
		setAssignPartnersModal(false);
		dispatch('SELECT_GAME', { game: null });
		dispatch('FORM_PARTNERS_RESET');
	};

	const bulkOptions: { label: string; value: string }[] = [
		{ label: 'Import Games', value: 'import_games' },
		{ label: 'Bulk Assign Partners', value: 'bulk_assign_partners' },
		{ label: 'Bulk Enable Games', value: 'bulk_enable_games' },
	];

	const onBulkAction = (val: string) => {
		switch (val) {
			case 'import_games':
				setImportGameModal(true);
				break;
			case 'bulk_assign_partners':
				setBulkAssignPartnersModal(true);
				break;
			case 'bulk_enable_games':
				setBulkEnableGamesModal(true);
				break;
			default:
				break;
		}
	};

	const onCloseShowPartners = () => {
		setShowPartnersModal(false);
		dispatch('SELECT_GAME', { game: null });
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth onlyAdmin permission={PERMISSIONS.canViewOwnDetails}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={gamesPages} title='Games' />
					</Sidebar>
					<Page>
						<PageHeader title='Casino Games'>
							{user?.permissions?.isSuperAdmin ? (
								<Button dataId='create-game' primary small onClick={onAddGame}>
									<PlusLight className={styles.AddUserIcon} />
									Create Game
								</Button>
							) : (
								<></>
							)}
							{user?.permissions?.isSuperAdmin ? (
								<Dropdown
									options={bulkOptions}
									label='Bulk Options'
									className={styles.bulkDropdown}
									onChange={(val) => onBulkAction(val.value)}
									right={true}
								></Dropdown>
							) : (
								<></>
							)}
						</PageHeader>
						<section className={styles.PageContent}>
							<Filters>
								<div>
									<PartnersFilter />
									<CategoriesFilter />
									<ProvidersFilter />
									<GeneralFilter filterName='is_slot' dataTestId={'is_slot-filter'} />
									<GeneralFilter filterName='active' isMultiple={false} dataTestId={'active-filter'} />
									{process.env.REACT_APP_SITE_TITLE?.includes('Italy') && (
										<GeneralFilter filterName='checksumStatuses' dataTestId={'checksum_statuses-filter'} />
									)}
								</div>
								<div style={{ marginLeft: 'auto' }}>
									<SearchFilter searchTypes={filterSearchOptionsByEnv(CASINO_GAMES_SEARCH_TYPES)} />
								</div>
							</Filters>
							<div className={styles.BulkContainer}>{!!bulkRows.length && <BulkActions />}</div>
							{(!!games?.length || gamesLoading) && (
								<>
									{rows.length > 0 && (
										<TableActions
											exportData={{
												api: getCasinoGamesExport,
												csvHeaders: filteredGamesFields,
												filtersQuery: getFiltersUrl(filters),
											}}
											filters={[tableActions.EXPORT]}
										/>
									)}
									<Table
										titles={columns}
										data={rows}
										className={styles.GamesTable}
										rowClassName={styles.GamesRow}
										isLoading={gamesLoading}
									/>
									<Pagination
										total={Math.ceil(pagesTotal / +perPage)}
										current={currentPage}
										onChange={onPageChange}
										perPage={perPage}
										showPerPage
										onPerPage={onPerPageChange}
									/>
								</>
							)}
							{categoryModal && (
								<Modal
									title='Category'
									submitLabel={SAVE}
									primary
									onCancel={onCategoryModalClose}
									onSubmit={onAddCategory}
								>
									<AddCategoryForm />
								</Modal>
							)}
							{ImportGameModal && (
								<Modal
									title='Import Games'
									submitLabel='Import'
									primary
									onCancel={onImportGameModalClose}
									onSubmit={onImportGameSubmit}
								>
									<ImportGameForm />
								</Modal>
							)}
							{BulkAssignPartnersModal && (
								<Modal
									title='Bulk Assign Partners'
									submitLabel='Assign Partners'
									primary
									onCancel={onBulkAssignPartnersModalClose}
									onSubmit={onBulkAssignPartnersSubmit}
								>
									<BulkAssignPartnersForm />
								</Modal>
							)}
							{BulkEnableGamesModal && (
								<Modal
									title='Bulk Enable Games'
									submitLabel='Bulk Enable Games'
									primary
									onCancel={onBulkEnableGamesModalClose}
									onSubmit={onBulkEnableGamesSubmit}
								>
									<BulkEnableGamesForm />
								</Modal>
							)}
							{editModal && (
								<Modal
									title={selectedGame ? 'Edit Game' : 'Create Game'}
									submitLabel={SAVE}
									primary
									onCancel={onCloseCreateModal}
									onSubmit={onSubmit}
									loading={!loading && !isFormValid}
								>
									<CreateGameForm isEdit={!!selectedGame} onValidityChange={setIsFormValid} />
								</Modal>
							)}
							{assignPartnersModal && (
								<Modal
									title={'Assign partners'}
									submitLabel={SAVE}
									primary
									onCancel={onCloseAssignPartners}
									onSubmit={onAssignPartnersSubmit}
								>
									<AssignPartnersForm onValidityChange={setIsFormValid} />
								</Modal>
							)}
							{showPartnersModal && (
								<Modal title={'Show Game Partners'} primary onCancel={onCloseShowPartners} confirm={true}>
									<ShowPartners />
								</Modal>
							)}
						</section>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
		</>
	);
};

export { Games };
